import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createOrder, applyGiftCard, createCheckoutSession } from '../services/api';
import PageLayout from '../components/PageLayout';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'react-toastify';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from '../components/ui/dialog';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedAddressId, cart, couponDiscount } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isGiftCardModalOpen, setGiftCardModalOpen] = useState(false);
  const [giftCardCode, setGiftCardCode] = useState('');
  const [giftCardDiscount, setGiftCardDiscount] = useState(0);
  const madCoinsDiscount = cart?.madCoinsToUse || 0;

  // When returning from Stripe (cancel/back), location.state is empty. Redirect to cart.
  // Stripe cancel_url is /checkout?canceled=true – we send user to /cart with that so Cart can show a message.
  useEffect(() => {
    if (!cart) {
      const search = location.search || '';
      const target = search.includes('canceled=true') ? '/cart?canceled=true' : '/cart';
      navigate(target, { replace: true });
    }
  }, [cart, location.search, navigate]);

  const shippingCost = cart?.shippingCost ?? (cart?.total > 100 ? 0 : 4); // Use backend value or fallback
  const totalAfterCoupon = (cart?.total || 0) - (couponDiscount || 0);
  const totalAfterMadCoins = Math.max(0, totalAfterCoupon - madCoinsDiscount);
  const grandTotal = Math.round((totalAfterMadCoins + shippingCost - giftCardDiscount) * 100) / 100;

  const handleApplyGiftCard = async () => {
    try {
      const response = await applyGiftCard(giftCardCode);
      if (response.success) {
        const remainingBalance = response.giftCard.currentValue;
        const amountToPay = totalAfterCoupon + shippingCost;
        const discountToApply = Math.min(remainingBalance, amountToPay);
        setGiftCardDiscount(discountToApply);
        toast.success('Gift card applied successfully');
        setGiftCardModalOpen(false);
      } else {
        toast.error(response.error);
      }
    } catch (error) {
      toast.error('Failed to apply gift card');
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!selectedAddressId) {
      setError('No shipping address selected. Please go back to the cart.');
      setLoading(false);
      return;
    }

    try {
      // Validate shipping address ID format (MongoDB ObjectId)
      if (!selectedAddressId || typeof selectedAddressId !== 'string' || selectedAddressId.length !== 24) {
        setError('Invalid shipping address. Please select a valid address from the cart page.');
        setLoading(false);
        return;
      }

      // Create order in the backend
      const response = await createOrder({
        shippingAddressId: selectedAddressId,
        couponDiscount: couponDiscount || 0,
        giftCardDiscount: giftCardDiscount || 0,
        madCoinsDiscount: madCoinsDiscount || 0,
      });

      if (response.success) {
        // Create Stripe checkout session
        const checkoutResponse = await createCheckoutSession(
          response.order._id,
          grandTotal,
          'gbp'
        );

        if (checkoutResponse.success) {
          // Redirect to Stripe checkout
          window.location.href = checkoutResponse.url;
        } else {
          setError(checkoutResponse.error || 'Failed to create payment session.');
        }
      } else {
        // Show detailed error message from backend validation
        const errorMsg = response.error || 'Failed to create order.';
        setError(errorMsg);
        console.error('Order creation failed:', response);
      }
    } catch (err) {
      console.error('Order creation error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!cart) {
    return (
      <PageLayout>
        <div className="max-w-2xl mx-auto p-4 text-center">
          <p>Redirecting to cart…</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto p-4">
        <Button variant="link" onClick={() => navigate('/cart')}>
          &larr; Back to Cart
        </Button>
        <Card>
          <CardHeader>
            <CardTitle>Checkout</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <strong>Shipping:</strong> We currently ship only to the United Kingdom (England, Wales, Scotland, Northern Ireland, Isle of Man, and Isle of Skye). Orders above £100 get free shipping, otherwise a £4 fee applies.
              </div>
              <h3 className="text-lg font-semibold">Order Summary</h3>
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>£{cart.total.toFixed(2)}</span>
              </div>
              {couponDiscount > 0 && (
                <div className="flex justify-between">
                  <span>Coupon Discount:</span>
                  <span>- £{couponDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Shipping:</span>
                <span>£{shippingCost.toFixed(2)}</span>
              </div>
              {madCoinsDiscount > 0 && (
                <div className="flex justify-between">
                  <span>MAD Coins Discount:</span>
                  <span>- £{madCoinsDiscount.toFixed(2)}</span>
                </div>
              )}
              {giftCardDiscount > 0 && (
                <div className="flex justify-between">
                  <span>Gift Card Discount:</span>
                  <span>- £{giftCardDiscount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-lg">
                <span>Total:</span>
                <span>£{grandTotal.toFixed(2)}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                You will earn MAD Coins based on your order total after placing the order.
              </p>
              <Button variant="outline" onClick={() => setGiftCardModalOpen(true)}>Use Gift Card</Button>
            </div>
          </CardContent>
          <form onSubmit={handlePayment}>
            <CardFooter>
              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Processing...' : 'Continue to Payment'}
              </Button>
            </CardFooter>
          </form>
          {error && <p className="text-sm text-red-500 p-4">{error}</p>}
        </Card>
      </div>
      <Dialog open={isGiftCardModalOpen} onOpenChange={setGiftCardModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Apply Gift Card</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <Input
              placeholder="Enter gift card code"
              value={giftCardCode}
              onChange={(e) => setGiftCardCode(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button onClick={handleApplyGiftCard}>Apply</Button>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

export default CheckoutPage;
